/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-03
 */

// Timezone options with user-friendly names
export interface TimezoneOption {
  value: string; // Technical timezone identifier
  label: string; // User-friendly display name
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'America/Halifax', label: 'Atlantic Time (AT)' },
  { value: 'America/St_Johns', label: 'Newfoundland Time (NT)' },
  { value: 'UTC', label: 'Coordinated Universal Time (UTC)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Europe/Moscow', label: 'Moscow Time (MSK)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Kolkata', label: 'India Standard Time (IST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'Australia/Perth', label: 'Australian Western Time (AWT)' },
];

// Studio type options with descriptions
export interface StudioTypeOption {
  value: 'personal' | 'business' | 'organization';
  label: string;
  description: string;
}

export const STUDIO_TYPE_OPTIONS: StudioTypeOption[] = [
  {
    value: 'personal',
    label: 'Personal Studio',
    description: 'For individual instructors and small operations',
  },
  {
    value: 'business',
    label: 'Business Studio',
    description: 'For established dance studios and companies',
  },
  {
    value: 'organization',
    label: 'Organization Studio',
    description: 'For schools, community centers, and institutions',
  },
];

// Helper function to get timezone label from value
export const getTimezoneLabel = (timezoneValue?: string): string => {
  if (!timezoneValue) return 'Not set';

  const option = TIMEZONE_OPTIONS.find(tz => tz.value === timezoneValue);
  return option ? option.label : timezoneValue.replace('_', ' ');
};

// Helper function to get studio type label from value
export const getStudioTypeLabel = (studioTypeValue?: string): string => {
  if (!studioTypeValue) return 'Studio';

  const option = STUDIO_TYPE_OPTIONS.find(
    type => type.value === studioTypeValue
  );
  return option ? option.label : 'Studio';
};
